import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link2, FileText, Share2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { formatRoutineAsText } from '../utils/formatRoutineAsText';

function buildShareUrl(routineId) {
  return `${window.location.origin}/routine/${routineId}`;
}

function canNativeShare(payload) {
  return typeof navigator !== 'undefined'
    && typeof navigator.share === 'function'
    && (typeof navigator.canShare !== 'function' || navigator.canShare(payload));
}

/**
 * RoutineShareMenu — wraps a trigger element with a dropdown menu that lets the
 * user copy the routine as a link, copy it as Markdown text, or open the native
 * OS share sheet (when supported).
 *
 * Props:
 *  - routine: routine object (must have id; name + days for full content)
 *  - children: trigger element (e.g., <Button><Share2/></Button>)
 *  - align?: 'start' | 'end' (default 'end')
 */
export default function RoutineShareMenu({ routine, children, align = 'end' }) {
  const { t } = useTranslation();

  const copyLink = async (e) => {
    e?.stopPropagation?.();
    try {
      await navigator.clipboard.writeText(buildShareUrl(routine.id));
      toast.success(t('routineShare.toast.linkCopied'));
    } catch {
      toast.error(t('routineShare.toast.copyFailed'));
    }
  };

  const copyText = async (e) => {
    e?.stopPropagation?.();
    try {
      const text = formatRoutineAsText(routine, t);
      await navigator.clipboard.writeText(text);
      toast.success(t('routineShare.toast.textCopied'));
    } catch {
      toast.error(t('routineShare.toast.copyFailed'));
    }
  };

  const sharePayload = {
    title: routine?.name || 'trainedbyRS routine',
    text: formatRoutineAsText(routine, t),
    url: buildShareUrl(routine.id),
  };
  const showNative = canNativeShare(sharePayload);

  const shareNative = async (e) => {
    e?.stopPropagation?.();
    try {
      await navigator.share(sharePayload);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      toast.error(t('routineShare.toast.copyFailed'));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={copyLink} data-testid="routine-share-copy-link">
          <Link2 className="mr-2 h-4 w-4" />
          {t('routineShare.menu.copyLink')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={copyText} data-testid="routine-share-copy-text">
          <FileText className="mr-2 h-4 w-4" />
          {t('routineShare.menu.copyText')}
        </DropdownMenuItem>
        {showNative && (
          <DropdownMenuItem onClick={shareNative} data-testid="routine-share-native">
            <Share2 className="mr-2 h-4 w-4" />
            {t('routineShare.menu.shareNative')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
